import React from "react";
import PlusIcon from "../../Images/PlusIcon.svg";
import { useOutletContext, useNavigate } from "react-router-dom";
import Group_tic from "../../Images/Group_tic.svg";

const MyProgram = () => {
  const navigate = useNavigate();
  const { userData } = useOutletContext() || {};
  const dashboard = userData?.dashboard || {};

  // Safe array defaults
  const scheduledPrograms = dashboard?.scheduled_programs_list || [];
  const nonScheduledPrograms = dashboard?.non_scheduled_programs_list || [];

  const allPrograms = [
    ...scheduledPrograms.map((p) => ({ ...p, isScheduled: true })),
    ...nonScheduledPrograms.map((p) => ({ ...p, isScheduled: false })),
  ];

  const handleClick = () => {
    navigate("/ins_program/create_program");
  };

  const handleProgramClick = (program) => {
    console.log("Course ID:", program.course_id);
    console.log("Schedule ID:", program.schedule_id);
    navigate("/ins_program/module_track", {
      state: { courseId: program.course_id, schedule_id: program.schedule_id },
    });
  };

  // ✅ Use string-based date comparison to avoid timezone issues
  const todayStr = new Date().toISOString().split("T")[0]; // e.g. "2026-03-12"

  // 🔹 Check if discount is currently valid
  const isDiscountValid = (schedule) => {
    if (!schedule || !schedule.discount_applicable || !schedule.discount_value) {
      return false;
    }

    if (!schedule.discount_valid_from && !schedule.discount_valid_to) {
      return true;
    }

    const now = new Date();
    const validFrom = schedule.discount_valid_from
      ? new Date(schedule.discount_valid_from)
      : null;
    const validTo = schedule.discount_valid_to
      ? new Date(schedule.discount_valid_to)
      : null;

    if (validFrom && now < validFrom) return false;
    if (validTo && now > validTo) return false;

    return true;
  };

  // 🔹 Calculate discounted price based on discount type
  const calculateDiscountedPrice = (schedule, originalPrice) => {
    if (!isDiscountValid(schedule)) {
      return originalPrice;
    }

    const discountType = schedule.discount_type;
    const discountValue = Number(schedule.discount_value);
    const price = Number(originalPrice);

    if (discountType === "Percentage") {
      const discountAmount = (price * discountValue) / 100;
      return price - discountAmount;
    } else if (discountType === "FlatAmount") {
      return Math.max(0, price - discountValue);
    }

    return originalPrice;
  };

  // Prepare program cards safely
  const programCards = allPrograms.map((program) => {
    const course = program?.course || program || {};
    const currentSchedule = program?.isScheduled ? program : null;

    const startDateRaw = currentSchedule?.start_date || course?.start_date;
    const startDate = startDateRaw
      ? new Date(startDateRaw).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        })
      : "N/A";

    // ✅ Use plain date strings (YYYY-MM-DD) for comparison
    const startDateStr = startDateRaw
      ? startDateRaw.toString().split("T")[0]
      : null;
    const endDateStr = (currentSchedule?.end_date || course?.end_date)
      ? (currentSchedule?.end_date || course?.end_date).toString().split("T")[0]
      : null;

    // Determine right-side badge
    let badge = "";
    let badgeColor = "";

    if (program?.isScheduled) {
      if (startDateStr && startDateStr > todayStr) {
        // Course hasn't started yet
        badge = "Upcoming";
        badgeColor = "bg-blue-100 text-blue-800";
      } else if (endDateStr && endDateStr < todayStr) {
        // Course has ended
        badge = "Completed";
        badgeColor = "bg-gray-200 text-gray-700";
      } else {
        // Course is currently running
        badge = "Ongoing";
        badgeColor = "bg-emerald-100 text-lime-800";
      }
    }

    const originalPrice = course?.amount ? Number(course.amount) : 0;
    const discountedPrice = currentSchedule
      ? calculateDiscountedPrice(currentSchedule, originalPrice)
      : originalPrice;
    const hasDiscount =
      currentSchedule &&
      isDiscountValid(currentSchedule) &&
      discountedPrice < originalPrice;

    return {
      course_id: course?.course_id || 0,
      title: course?.title || "Untitled",
      description: course?.description || "",
      course_code: course?.course_code || "",
      level: course?.level || "",
      language: course?.language || "",
      course_type: course?.course_type || "",
      location: program?.isScheduled
        ? currentSchedule?.location || course?.location || "N/A"
        : course?.location || "N/A",
      image:
        course?.image ||
        course?.program_thumbnail ||
        `https://placehold.co/270x201?text=${encodeURIComponent(
          course?.title || "Course",
        )}`,
      start_date: currentSchedule?.start_date || course?.start_date || null,
      end_date: currentSchedule?.end_date || course?.end_date || null,
      startDate,
      created_at: course?.created_at || null,
      priceOld: originalPrice,
      priceNew: discountedPrice,
      hasDiscount,
      badge,
      badgeColor,
      modules: `${course?.total_modules || 0} Modules`,
      duration: currentSchedule?.total_duration_days
        ? `${currentSchedule.total_duration_days} Days`
        : "N/A",
      isScheduled: program?.isScheduled || false,
      schedule_id: currentSchedule?.schedule_id || null,
    };
  });

  // ✅ Derive counts from programCards (not from backend which returns 0)
  const completedCount = programCards.filter((p) => p.badge === "Completed").length;
  const ongoingCount = programCards.filter((p) => p.badge === "Ongoing").length;

  // Dashboard stats
  const statsCards = [
    {
      title: "Created Programs",
      value:
        (dashboard?.scheduled_programs || 0) +
        (dashboard?.non_scheduled_programs || 0),
    },
    { title: "Scheduled Programs", value: dashboard?.scheduled_programs || 0 },
    { title: "Ongoing Programs", value: ongoingCount },       // ✅ derived from cards
    { title: "Completed Programs", value: completedCount },   // ✅ derived from cards
  ];

  return (
    <div className="w-full flex flex-col gap-6 p-6 bg-gray-50">
      {/* HEADER */}
      <div className="w-full flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <h2 className="text-zinc-800 text-2xl font-bold">My Program</h2>
        <button
          onClick={handleClick}
          className="px-4 py-2 flex bg-[#4AACC5] hover:bg-teal-600 text-white text-sm font-semibold rounded-lg items-center gap-2"
        >
          <img src={PlusIcon} alt="" className="w-4 h-4" />
          Create Program
        </button>
      </div>

      {/* DASHBOARD CARDS */}
      <div className="flex flex-wrap gap-6">
        {statsCards.map((card, i) => (
          <div
            key={i}
            className="w-full sm:w-56 h-28 bg-white rounded shadow flex items-center px-4 gap-3"
          >
            <div className="w-16 h-16 bg-cyan-100 rounded flex items-center justify-center">
              <img src={Group_tic} alt="" className="w-6 h-6" />
            </div>
            <div>
              <p className="text-sm font-medium">{card.title}</p>
              <p className="text-black text-2xl font-bold">{card.value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* PROGRAM CARDS */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
        {programCards.map((program, index) => (
          <div
            key={index}
            onClick={() => handleProgramClick(program)}
            className="bg-white rounded-lg shadow-lg overflow-hidden relative cursor-pointer hover:shadow-xl transition"
          >
            {/* Program Image */}
            <img
              src={program.image}
              alt={program.title}
              className="w-full h-52 object-cover"
            />

            {/* Card Body */}
            <div className="p-4 relative">
              {/* Left Side: Location */}
              <div className="absolute left-4 top-4 text-xs rounded-3xl px-3 py-1 bg-blue-100 text-blue-800">
                {program.location || "N/A"}
              </div>

              {/* Right Side: Status */}
              {program.isScheduled && program.badge && (
                <div
                  className={`absolute right-4 top-4 text-xs rounded-3xl px-3 py-1 ${program.badgeColor}`}
                >
                  {program.badge}
                </div>
              )}

              {/* Program Title */}
              <div className="text-gray-900 font-medium text-base mb-2 mt-8">
                {program.title || "Untitled"}
              </div>

              {/* Pricing */}
              <div className="flex gap-2 mb-2 text-sm">
                {program.hasDiscount ? (
                  <>
                    <span className="line-through text-red-500">
                      ${program.priceOld.toFixed(2)}
                    </span>
                    <span className="text-green-600 font-semibold">
                      ${program.priceNew.toFixed(2)}
                    </span>
                  </>
                ) : (
                  <span className="text-gray-900 font-semibold">
                    ${program.priceOld.toFixed(2)}
                  </span>
                )}
              </div>

              {/* Bottom Meta Info */}
              <div className="border-t border-zinc-200 pt-2">
                <div className="flex items-center gap-2 text-neutral-700 text-xs font-normal">
                  <span>{program.modules || "0 Modules"}</span>
                  <div className="w-px h-2 bg-stone-300 rotate-[25deg]" />
                  <span>{program.duration || "N/A"}</span>
                  <div className="w-px h-2 bg-stone-300 rotate-[25deg]" />
                  <span className="whitespace-nowrap truncate">
                    {program.startDate || "N/A"}
                  </span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default MyProgram;